// ドメイン非依存のヒートマップ。ApexCharts の heatmap を描画しつつ、視覚に依存しない
// 同等の表を併置する（WCAG2: 図にはテキスト代替を用意する）。
//
// 表は ui/data-table を再利用し、caption(sr-only) / `th scope="col"` / 行見出し
// `th scope="row"` / 空メッセージをそのまま継承する（マトリクス表なので行見出しが必要で、
// data-table 側に isRowHeader を足して満たした）。
// 表は既定 sr-only だが `focus-within:not-sr-only` でキーボードのフォーカスが入った時点で
// 可視化する。sr-only のままだと子孫のボタンはフォーカス可能なのに永久に不可視で、
// 晴眼のキーボード利用者が「見えない停止」を行数×列数ぶん踏むことになる（WCAG2 2.4.7）。
import ApexCharts from "apexcharts";
import type { ApexOptions } from "apexcharts";
import { createDataTable, type Column } from "./data-table";

export interface HeatmapCell {
  readonly column: string;
  readonly value: number;
}
export interface HeatmapRow {
  readonly row: string;
  readonly cells: readonly HeatmapCell[];
}
export interface HeatmapProps {
  readonly rows: readonly HeatmapRow[];
  readonly ariaLabel: string;
  // 列の同一性と順序。省略時は rows に現れた順の和集合。ApexCharts の x 軸は「系列ごとに
  // その系列の x から」組み立てられるため、行ごとに列集合や順序が違うと見た目のグリッドだけが
  // 静かにずれる（どの列の値なのかを取り違える）。全行をこの列リストへ射影して固定する。
  readonly columns?: readonly string[];
  // 行見出し列のヘッダ名（既定「項目」）。
  readonly rowHeader?: string;
  // セル値の表示形式（既定は素の数値）。単位（「名」など）はドメイン側が与える。
  readonly formatValue?: (value: number) => string;
  readonly emptyMessage?: string;
  readonly height?: number;
  readonly onCellClick?: (row: string, column: string) => void;
  readonly ctor?: typeof ApexCharts; // テスト注入用
}

// 射影で埋まらなかったセル。0（=該当なし）と偽らず「取得できていない」と明示する。
const MISSING_LABEL = "データなし";

// 配色は東雲パレット（styles.css の @theme）から。ApexCharts の heatmap 既定は
// enableShades:true + dataLabels 白文字で、値が小さいセルはほぼ白まで明るくされるため
// 白文字が読めなくなる（0 のセルは実質 100% 明色化）。陰影を切って範囲ごとに背景色と
// 文字色（foreColor）を明示し、0 と 1 以上を意図的に区別する。
const ZERO_COLOR = "#f1f5f9"; // slate-100
const ZERO_TEXT = "#475569"; // slate-600
const NEGATIVE_COLOR = "#cbd5e1"; // slate-300
const DARK_TEXT = "#0f172a"; // slate-900
const BANDS: readonly { readonly color: string; readonly foreColor: string }[] = [
  { color: "#fed7aa", foreColor: DARK_TEXT }, // shinonome-200
  { color: "#fb923c", foreColor: DARK_TEXT }, // shinonome-400
  { color: "#a34a00", foreColor: "#ffffff" }, // shinonome-700（白文字で 4.5:1）
];

interface ColorRange {
  readonly from: number;
  readonly to: number;
  readonly color: string;
  readonly foreColor: string;
}

// 実データの範囲を必ず覆う配色範囲を作る（範囲外の値は ApexCharts 既定の白文字に戻るため）。
function buildColorRanges(min: number, max: number): ColorRange[] {
  const ranges: ColorRange[] = [];
  if (min < 0) ranges.push({ from: min, to: -1, color: NEGATIVE_COLOR, foreColor: DARK_TEXT });
  ranges.push({ from: 0, to: 0, color: ZERO_COLOR, foreColor: ZERO_TEXT });
  if (max >= 1) {
    const b1 = Math.max(1, Math.ceil(max / 3));
    const b2 = Math.max(b1, Math.ceil((max * 2) / 3));
    const bounds: readonly (readonly [number, number])[] = [
      [1, b1],
      [b1 + 1, b2],
      [b2 + 1, max],
    ];
    bounds.forEach(([from, to], i) => {
      const band = BANDS[i];
      if (band !== undefined && from <= to) {
        ranges.push({ from, to, color: band.color, foreColor: band.foreColor });
      }
    });
  }
  return ranges;
}

function deriveColumns(rows: readonly HeatmapRow[]): string[] {
  const seen = new Set<string>();
  for (const r of rows) for (const c of r.cells) seen.add(c.column);
  return [...seen];
}

export function createHeatmap(props: HeatmapProps): HTMLElement {
  const fmt = props.formatValue ?? ((v: number): string => String(v));
  const columns = props.columns ?? deriveColumns(props.rows);

  const wrap = document.createElement("div");
  wrap.setAttribute("role", "group");
  wrap.setAttribute("aria-label", props.ariaLabel);

  // 全行を同一の列リストへ射影する（列の同一性を固定）。欠けている列は null=未取得。
  const valuesByRow = new Map<HeatmapRow, readonly (number | null)[]>();
  const matrix: readonly (number | null)[][] = props.rows.map((r) => {
    const byColumn = new Map(r.cells.map((c) => [c.column, c.value]));
    const values = columns.map((col) => byColumn.get(col) ?? null);
    valuesByRow.set(r, values);
    return values;
  });

  // 描くべき値が無い状態。空のチャート＋空の表を並べると「該当なし」に見えてしまうため、
  // 表を可視にして明示的な空メッセージだけを出し、チャートは作らない。
  const noData = props.rows.length === 0 || columns.length === 0;

  const rowHeaderColumn: Column<HeatmapRow> = {
    key: "__row",
    header: props.rowHeader ?? "項目",
    isRowHeader: true,
    cell: (r) => r.row,
  };
  const valueColumns: Column<HeatmapRow>[] = columns.map((col, ci) => ({
    key: col,
    header: col,
    cell: (r: HeatmapRow): HTMLElement | string => {
      const value = valuesByRow.get(r)?.[ci] ?? null;
      if (value === null) return MISSING_LABEL;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.dataset["testid"] = "heatmap-cell";
      btn.dataset["row"] = r.row;
      btn.dataset["column"] = col;
      // 可視テキストは値のみ（列名は th scope=col が持つ）。ただし単体でフォーカスされた
      // ボタンは自身の名前しか読まれないため、読み上げ名には行・列・値を持たせる。
      btn.textContent = fmt(value);
      btn.setAttribute("aria-label", `${r.row} ${col} ${fmt(value)}`);
      btn.className = "underline decoration-dotted";
      const onCellClick = props.onCellClick;
      if (onCellClick !== undefined) {
        btn.addEventListener("click", () => onCellClick(r.row, col));
      }
      return btn;
    },
  }));

  const table = createDataTable<HeatmapRow>({
    // group の aria-label と同一文言にすると同じ文字列が二重に読み上げられるため、
    // 「表形式の等価物」であることを足して区別する。
    caption: `${props.ariaLabel}（表形式）`,
    columns: noData ? [rowHeaderColumn] : [rowHeaderColumn, ...valueColumns],
    rows: noData ? [] : props.rows,
    emptyMessage: props.emptyMessage ?? "データがありません。",
  });

  if (noData) {
    wrap.append(table);
    return wrap;
  }

  // 図が見えている間、表は視覚的には冗長なので sr-only。キーボードで入ったら可視化する。
  table.classList.add("sr-only", "focus-within:not-sr-only");

  const mount = document.createElement("div");
  mount.setAttribute("aria-hidden", "true");
  wrap.append(mount, table);

  const present = matrix.flat().filter((v): v is number => v !== null);
  const min = present.length > 0 ? Math.min(...present) : 0;
  const max = present.length > 0 ? Math.max(...present) : 0;

  const Ctor = props.ctor ?? ApexCharts;
  const options: ApexOptions = {
    chart: {
      type: "heatmap",
      height: props.height ?? 240,
      animations: { enabled: false },
      toolbar: { show: false },
      events: {
        // ApexCharts が渡す options は seriesIndex / dataPointIndex を含む（型は
        // ApexChartEventOpts）。ここで必要なのはその 2 つだけなので絞った型で受ける。
        dataPointSelection: (
          _e: MouseEvent,
          _ctx: ApexCharts | undefined,
          cfg?: { seriesIndex: number; dataPointIndex: number },
        ): void => {
          if (cfg === undefined) return;
          const r = props.rows[cfg.seriesIndex];
          const col = columns[cfg.dataPointIndex];
          // 未取得セルは表でもボタンにしていないので、チャートからも選択させない。
          const value = matrix[cfg.seriesIndex]?.[cfg.dataPointIndex];
          if (r === undefined || col === undefined || value === undefined || value === null) return;
          props.onCellClick?.(r.row, col);
        },
      },
    },
    series: props.rows.map((r, ri) => ({
      name: r.row,
      data: columns.map((col, ci) => ({ x: col, y: matrix[ri]?.[ci] ?? null })),
    })),
    plotOptions: { heatmap: { enableShades: false, colorScale: { ranges: buildColorRanges(min, max) } } },
    dataLabels: {
      enabled: true,
      // 範囲外の値に当たった場合の保険（通常は range.foreColor が使われる）。既定の白文字は
      // 淡いセルで読めないため濃色にする。
      style: { colors: [DARK_TEXT] },
      // ApexCharts の型は string | number | number[] を渡し得る宣言（heatmap では number |
      // null）。未取得セル(null)はラベルを出さない＝0 と誤読させない。
      formatter: (v: string | number | number[] | null) => (typeof v === "number" ? fmt(v) : ""),
    },
    colors: [DARK_TEXT], // colorScale が全域を覆うため通常は使われない
    legend: { show: false },
    tooltip: { y: { formatter: (v: number | null) => (v === null ? MISSING_LABEL : fmt(v)) } },
  };

  // gauge.ts と同様、ApexCharts は描画時にコンテナが DOM 接続済みであることを要求する。
  // create* は要素を返すだけで呼び出し側が後から append するため、append 完了後に描画する。
  // 背面タブでも描画されるよう requestAnimationFrame ではなく queueMicrotask を使う。
  queueMicrotask(() => {
    try {
      const chart = new Ctor(mount, options);
      void chart.render();
    } catch (e) {
      console.error("[heatmap] render failed", e);
    }
  });

  return wrap;
}
